import { reduceAsync, isActionItem } from 'common/utils/index';
import { SearchStation, Station } from 'dataSources/Typicode/stations/types';
import binarySearch from 'common/utils/binarySearch';
import { BBox } from '@turf/helpers';
import { turfLineString } from 'common/utils/index';
import turfBbox from '@turf/bbox';

interface MappingStationCodesResult {
  stations: Station[];
  actionStations: Station[];
  stationsBBox: BBox;
  actionStationsBBox: BBox;
  serviceCodes: number[];
}

interface ReduceResult
  extends Pick<
    MappingStationCodesResult,
    'stations' | 'actionStations' | 'serviceCodes'
  > {
  stationCoords: [
    Station['coordinates']['longitude'],
    Station['coordinates']['latitude'],
  ][];
  actionStationCoords: [
    Station['coordinates']['longitude'],
    Station['coordinates']['latitude'],
  ][];
}

export const mappingStationCodes = async (
  stationCodes: SearchStation[],
  stations: Station[],
): Promise<MappingStationCodesResult> => {
  const result = await reduceAsync<SearchStation, ReduceResult>(
    stationCodes,
    (acc, stationCode) => {
      const findStation = binarySearch<Station>(
        { key: 'code', value: stationCode.code },
        stations,
      );

      const station =
        findStation && ({ ...findStation, ...stationCode } as Station);

      if (station && station.code && station.coordinates) {
        acc.stations.push(station);
        acc.stationCoords.push([
          station.coordinates.longitude,
          station.coordinates.latitude,
        ]);

        station.roadsideServiceCodes.forEach((code) => {
          if (!acc.serviceCodes.has(code)) {
            acc.serviceCodes.set(code);
          }
        });

        if (isActionItem(station)) {
          acc.actionStations.push(station);
          acc.actionStationCoords.push([
            station.coordinates.longitude,
            station.coordinates.latitude,
          ]);
        }
      }
      return acc;
    },
    {
      stations: [],
      actionStations: [],
      stationCoords: [],
      actionStationCoords: [],
      serviceCodes: new Map(),
    },
  );

  return {
    ...result,
    stationsBBox: turfBbox(turfLineString(result.stationCoords)),
    actionStationsBBox: turfBbox(turfLineString(result.actionStationCoords)),
    serviceCodes: Array.from(result.serviceCodes.keys()),
  };
};
